/**
 * Layer
 *
 * @class Layer
 */
export default class Layer {
    /**
     * @type {string}
     */
    name = null;

    /**
     * @type {SVGWrapper}
     */
    svg = null;

    /**
     * @type {SVGGElement}
     */
    dom = null;

    /**
     * @type {jQuery}
     */
    element = null;

    /**
     * @type {Map.<string, Layer>}
     */
    children = new Map();

    /**
     * Initializes the layer manager
     *
     * @constructor
     * @param {string} name Name
     * @param {SVGWrapper} svg SVG Wrapper
     * @param {SVGGElement} dom DOM
     * @param {jQuery} element Element
     */
    constructor(name, svg, dom, element) {
        this.name = name;
        this.svg = svg;
        this.dom = dom;
        this.element = element;
    }

    /**
     * @return {string}
     */
    getName() {
        return this.name;
    }

    /**
     * @return {SVGGElement}
     */
    getDom() {
        return this.dom;
    }

    /**
     * @return {jQuery}
     */
    getElement() {
        return this.element;
    }

    /**
     * @return {SVGWrapper}
     */
    getSvg() {
        return this.svg;
    }

    /**
     * @return {SVGDefs}
     */
    getDefs() {
        if (!this.defs) {
            this.defs = this.svg.defs(this.dom);
        }

        return this.defs;
    }

    /**
     * @param {Layer} childLayer
     */
    setChildLayer(childLayer) {
        if (!(childLayer instanceof Layer)) {
            throw new Error('Child layer has to be of type Layer.');
        }

        this.children.set(childLayer.getName(), childLayer);
    }

    /**
     * @param {string} name
     * @return {Layer}
     */
    getChildLayer(name) {
        if (!this.children.has(name)) {
            throw new Error(`Child layer ${name} not found.`);
        }
        return this.children.get(name);
    }

    /**
     * @param {string} name
     */
    removeChildLayer(name) {
        if (!this.children.has(name)) {
            throw new Error(`Child layer ${name} not found.`);
        }
        this.children.get(name).getElement().remove();
        this.children.delete(name);
    }

    eachChildLayer(fn) {
        if (typeof fn !== 'function') return;

        this.children.forEach(fn);
    }

    /**
     * @param {string} name
     * @return {Layer|null}
     */
    findChildLayer(name) {
        return this.children.get(name) || null;
    }

    /**
     * Show layer
     */
    show() {
        this.element.attr('visibility', 'visible');
    }

    /**
     * Hide layer
     */
    hide() {
        this.element.attr('visibility', 'hidden');
    }

    group(...args) {
        return this.svg.group(this.getElement(), ...args);
    }

    image(...args) {
        return this.svg.image(this.getElement(), ...args);
    }

    text(...args) {
        return this.svg.text(this.getElement(), ...args);
    }

    path(...args) {
        return this.svg.path(this.getElement(), ...args);
    }

    mask(...args) {
        return this.svg.mask(this.getElement(), ...args);
    }

    clipPath(...args) {
        return this.svg.clipPath(this.getElement(), ...args);
    }

    line(...args) {
        return this.svg.line(this.getElement(), ...args);
    }

    rect(...args) {
        return this.svg.rect(this.getElement(), ...args);
    }

    circle(...args) {
        return this.svg.circle(this.getElement(), ...args);
    }
}
